import React, { useRef } from 'react';
import ReactTable from 'react-table';
import { Button } from 'rebass/styled-components';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import CenteredSpinner from 'features/ui/CenteredSpinner';
import Tooltip from 'shared/components/atoms/Tooltip/NewTooltip';
import { Box, Flex, Text } from 'shared/components/display';

import { FACEBOOK_COPY } from 'shared/config/copy';
import { FORM_COPY } from 'shared/config/copy/formCopy';
import COLORS from 'shared/styles/colors';
import { TEXT_VARIANTS } from 'shared/styles/text';
import theme from 'shared/styles/themes';

import { AdCountLedger } from '../../Campaigns';

import styles from './styles';
import { AdCreative } from './types';

interface Props {
  ads: AdCreative[];
  adCountErrors: AdCountLedger[];
  loading: boolean;
  onClick: () => void;
}

export const UploadPrompt: React.FC<Props> = ({ ads, adCountErrors, loading, onClick }) => {
  const hasErrors = Boolean(adCountErrors.length);
  const hideErrorDisplay = !hasErrors;
  const disableUpload = loading || hasErrors;
  const ref = useRef<HTMLDivElement>(null);

  return (
    <Flex css={styles} className="prompt">
      <ul>
        {FACEBOOK_COPY.PROMPT_AD_UPLOAD_BODY.map((item, ndx) => (
          <li key={ndx}>{item}</li>
        ))}
      </ul>
      {loading && <CenteredSpinner height={40} width={40} py={1} />}
      <Box display={hideErrorDisplay ? 'none' : undefined} m={5}>
        <Text
          style={{
            marginBottom: theme.space[3],
          }}
          variant={TEXT_VARIANTS.ERROR}
        >
          This {ads.length} ad upload will exceed FB's ad limit for the following ad sets
        </Text>
        <ReactTable
          style={{ width: '75%' }}
          showPagination={false}
          minRows={0}
          columns={[
            {
              Header: 'Campaign Name',
              accessor: 'campaignName',
            },
            {
              Header: 'Adset Name',
              accessor: 'adsetName',
            },
            {
              Header: 'Current Ads',
              accessor: 'currentAds',
            },
            {
              Header: () => (
                <Flex className="prompt__ad-limit-header">
                  <Text>Ad Limit</Text>
                  <Tooltip
                    content="Facebook's ad limit per ad set is 50 ads for manual campaigns and 150 for Advantage+ sales campaigns."
                    portalTarget={ref.current}
                  >
                    <FontAwesomeIcon color={COLORS.NEUTRAL400} icon={faInfoCircle} size="sm" />
                  </Tooltip>
                </Flex>
              ),
              accessor: 'adLimit',
            },
            {
              Header: 'Active Ads',
              accessor: 'activeAds',
            },
            {
              Header: 'Paused Ads',
              accessor: 'pausedAds',
            },
            {
              Header: 'Ads to Remove',
              accessor: 'adsToRemove',
            },
          ]}
          data={adCountErrors.map((adCountError) => ({
            campaignName: adCountError?.campaignName,
            adsetName: adCountError?.adsetName,
            currentAds: adCountError?.total,
            activeAds: adCountError?.active.length,
            pausedAds: adCountError?.paused.length,
            adsToRemove: adCountError?.total - (adCountError.adLimit - ads?.length),
            adLimit: adCountError?.adLimit,
          }))}
        />
        <Text
          style={{
            marginTop: theme.space[3],
          }}
        >
          After removing the sufficient number of ads for each ad set above, close and re-submit.
        </Text>
      </Box>
      <Button m={5} disabled={disableUpload} onClick={onClick}>
        {FORM_COPY.UPLOAD_AD}
      </Button>
    </Flex>
  );
};
